import { useMemo, useState } from "react";
import type { ChartGrouping } from "@/hooks/use-auto-chart-interval";
import { useAutoChartInterval } from "@/hooks/use-auto-chart-interval";

type ColumnLike = {
  dataType?: string;
  key?: string | number;
};

interface UseDrilldownChartStateParams<
  TColumn extends ColumnLike = ColumnLike,
> {
  metricKey?: string | number | null;
  drilldownColumns?: TColumn[] | null;
  drilldownType?: string | null;
  rows?: unknown;
}

interface UseDrilldownChartStateResult<
  TColumn extends ColumnLike = ColumnLike,
> {
  viewMode: "table" | "chart";
  setViewMode: (next: "table" | "chart") => void;
  cumulative: boolean;
  setCumulative: (next: boolean) => void;
  dateColumns: TColumn[];
  typedRows: Record<string, unknown>[];
  hasDateColumns: boolean;
  showChartToggle: boolean;
  grouping: ChartGrouping;
  handleGroupingChange: (next: ChartGrouping) => void;
}

export function useDrilldownChartState<
  TColumn extends ColumnLike = ColumnLike,
>({
  metricKey,
  drilldownColumns,
  drilldownType,
  rows,
}: UseDrilldownChartStateParams<TColumn>): UseDrilldownChartStateResult<TColumn> {
  const [viewMode, setViewMode] = useState<"table" | "chart">("table");
  const [cumulative, setCumulative] = useState(false);

  const dateColumns = useMemo<TColumn[]>(() => {
    return (drilldownColumns ?? []).filter((col) => col?.dataType === "date");
  }, [drilldownColumns]);

  const typedRows = useMemo(() => {
    return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [];
  }, [rows]);

  const hasDateColumns = dateColumns.length > 0;
  const showChartToggle =
    drilldownType !== "chart" && hasDateColumns && typedRows.length > 0;

  const dateColumnRefs = useMemo(
    () =>
      dateColumns.map((col) => ({
        key: String(col?.key ?? ""),
      })),
    [dateColumns],
  );

  const { grouping, handleGroupingChange } = useAutoChartInterval({
    dateColumns: dateColumnRefs,
    dataRows: typedRows,
    metricKey,
  });

  return {
    viewMode,
    setViewMode,
    cumulative,
    setCumulative,
    dateColumns,
    typedRows,
    hasDateColumns,
    showChartToggle,
    grouping,
    handleGroupingChange,
  };
}
